// Angular
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
// Libraries
import { forkJoin, Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
// Afidium
import { SashaPageComponent } from '../sasha-page.component';
import { ViewsService } from 'libraries/view/views.service';
import { AppRootService } from 'app/shared/services/app-root/app-root-service';
import {
    IBookingIdentifier,
    IGetBookingsAirResponse,
    IGetVehicleRentalBookingOptions,
    ISectorAir,
    TGetBookingResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import {
    ISectorServicesConfig
} from 'libraries/component/booking-information/components/sectors/components/shared/interfaces/ISectorServicesConfig';
import { BookingResourceService } from 'libraries/service/resources/booking/bookingResource.service';
import { BookingType } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { ConfigurationResourceService } from 'libraries/service/resources/configuration/configurationResource.service';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { EnumsResourceService } from 'libraries/service/booking/enums/enums-resource.service';
import {
    ITableServices,
    TableServicesService
} from 'libraries/component/booking-information/components/sectors/components/shared/services/table-services/table-services.service';
import { SashaBreadcrumbService } from 'app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { AirBookingResourceService } from "libraries/service/resources/air-booking/air-booking-resource.service";
import { EAirBookingPermissions } from "libraries/component/booking-information/e-air-booking-permissions.enum";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { BookingAirToolsService } from "libraries/component/booking-information/services/booking-air-tools.service";
import { LanguageService } from "libraries/service/language/language.service";
import { VehicleRentalBookingService } from "libraries/service/booking/vehicleRentalBooking.service";
import { wenji } from "libraries/interfaces/IWenji";

@Component({
    selector: 'booking-details',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb *ngIf="bookingData"></sasha-breadcrumb>

            <notification *ngIf="notification.active"
                          [type]="notification.type">{{ notification.message }}</notification>
            <booking-information [tableServices]="tableServices"
                                 [bookingData]="bookingData"
                                 [bookingExtraData]="bookingExtraData"
                                 [bookingWithSelection]="bookingWithSelection"
                                 [serviceEnum]="serviceEnum"
                                 [isLoading]="isLoading"
                                 [isEditable]="editAvailable"
                                 [isLoadingServices]="isLoadingServices"
                                 [isLoadingServicesError]="isLoadingServicesError"
                                 [vehicleRentalNotification]="vehicleRentalNotification"
                                 [isVehicleRentalBooking]="isVehicleRentalBooking"
                                 (onBookingRefreshed)="reloadBooking($event)"></booking-information>
        </div>
    `,
    styles: [`
        .app-wrapper {
            max-width: 100%;
            margin-left: .75rem;
            margin-right: .75rem;
        }

        .container {
            display: flex;
        }

        .content {
            width: calc(100% - 245px);
            margin-left: 245px;
        }
    `]
})

export class BookingDetailsComponent extends SashaPageComponent implements OnInit {
    public bookingWithSelection: any;
    public isLoadingServices: boolean;
    public isLoadingServicesError: INotification = {
        active: false,
        type: NotificationType.Error,
    };

    public vehicleRentalNotification: INotification = {
        active: false,
        type: NotificationType.Warning,
    };

    public bookingData: TGetBookingResponse;
    public tableServices: ITableServices;
    public serviceEnum: IGetEnumsService[];
    public isLoading: boolean = false;
    public notification: INotification = {
        active: false,
    };
    public readonly labels: ILabels = {
        booking: Yii.t("wui-sasha-js", "Dossier"),
    };

    public editAvailable: boolean = true;
    public bookingExtraData: any = null;
    public locale: string = 'fr';
    public isVehicleRentalBooking: boolean = false;

    constructor(
        protected views: ViewsService,
        appRootService: AppRootService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly bookingResourceService: BookingResourceService,
        private readonly airBookingResourceService: AirBookingResourceService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly enumsResourceService: EnumsResourceService,
        private readonly tableServicesService: TableServicesService,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly titleService: TitleService,
        private readonly tracingService: ErrorTracingService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly bookingAirToolsService: BookingAirToolsService,
        private readonly languageService: LanguageService,
        private readonly vehicleRentalBookingService: VehicleRentalBookingService
    ) {
        super(views, appRootService);
        this.locale = this.languageService.getUserLanguage();
    }

    public ngOnInit(): void {
        this.titleService.setTitle(this.labels.booking);
        super.ngOnInit();

        this.isLoading = true;

        this.activatedRoute.params.pipe(
            tap((parameters: Params) => {
                if (parameters.processingid) {
                    this.sashaBreadcrumbService.setProcessingThenBooking(parameters.processingid, parameters.syscode);
                } else {
                    this.sashaBreadcrumbService.setBooking(parameters.syscode);
                }
            }),
            mergeMap((parameters: Params) => this.loadBooking(parameters.syscode)),
        ).subscribe({
            next: this.bookingLoadedNext.bind(this),
            error: this.bookingLoadedError.bind(this)
        });
    }

    public reloadBooking(reloadedBooking: IBookingIdentifier): void {
        this.isLoading = true;
        this.loadBooking(reloadedBooking.syscode)
            .subscribe({
                next: this.bookingLoadedNext.bind(this),
                error: this.bookingLoadedError.bind(this)
            });
    }

    private loadBooking(syscode: string): Observable<[TGetBookingResponse, IGetEnumsService[], ISectorServicesConfig]> {

        const getBookingObservable: Observable<TGetBookingResponse> = this.bookingResourceService
            .getBooking(
                syscode,
                {
                    computeCancellable: true,
                    computeIssuable: true,
                    computeVoidable: true,
                }
            );
        let canViewServices: boolean;
        this.isLoadingServices = false;
        this.isLoadingServicesError.active = false;
        return forkJoin([
            getBookingObservable,
            this.permissionResourceService.hasPermission(EAirBookingPermissions.SHARED_AIR_BOOKING_ANCILLARY_SERVICES_VIEW),
        ]).pipe(
            mergeMap(([bookingResponse, canViewServicesResponse]: [TGetBookingResponse, boolean]) => {
                canViewServices = canViewServicesResponse;
                return this.configurationBookingStream(bookingResponse);
            }),
            tap(([bookingResponse, enums, servicesConfig]: [
                TGetBookingResponse,
                IGetEnumsService[],
                ISectorServicesConfig,
            ]): void => {
                const isOneSectorLowCost: boolean = (((bookingResponse as IGetBookingsAirResponse).sector as ISectorAir[]) || [])
                    .some((sector: ISectorAir): boolean => sector.category === 'lowcost');

                if (bookingResponse?.type?.some((type: BookingType): boolean => type === BookingType.air) &&
                    (canViewServices && this.bookingAirToolsService.hasServices(bookingResponse as IGetBookingsAirResponse) || isOneSectorLowCost)) {

                    if(!bookingResponse.booking.serviceArray?.length){
                        this.isLoadingServices = true;
                        this.isLoadingServicesError.active = false;
                        this.airBookingResourceService.getWithServiceSelection(bookingResponse.host, bookingResponse.syscode, wenji.ui.target, true)
                            .subscribe({
                                next: (bookingWithSelection: any): void => {
                                    this.isLoadingServices = false;
                                    if (bookingWithSelection?.booking?.serviceArray?.length) {
                                        this.bookingWithSelection = bookingWithSelection;
                                    }
                                },
                                error: (err: Error): void => {
                                    this.isLoadingServices = false;
                                    this.isLoadingServicesError.active = true;
                                    this.isLoadingServicesError.message = Yii.t('wui-sasha-js', 'Erreur lors de la récupération des services : ') + err.message;
                                    console.error(err);
                                }
                            });
                    }else{
                        this.bookingWithSelection = bookingResponse;
                    }

                }
            })
        );
    }

    private bookingLoadedNext([bookingResponse, enums, servicesConfig, bookingWithSelection]: [
        TGetBookingResponse,
        IGetEnumsService[],
        ISectorServicesConfig,
        any,
    ]): void {
        this.bookingData = bookingResponse;
        this.serviceEnum = enums;
        this.titleService.setTitle(
            this.bookingData.code,
            this.labels.booking
        );
        this.tableServices = this.tableServicesService.getBookingTableServicesMapping(bookingResponse, servicesConfig, enums);

        if (this.vehicleRentalBookingService.isVehicleRentalBooking(bookingResponse)) {
            this.isLoading = true;
            this.isVehicleRentalBooking = true;
            const options: IGetVehicleRentalBookingOptions = {
                host: bookingResponse.host,
                languageCode: this.locale,
                format: 'xsl_direct_convert_xft2json'
            };
            this.bookingResourceService.getVehicleRentalBooking(bookingResponse.syscode, options).subscribe({
                next: (bookingExtraData: any): void => {
                    this.isLoading = false;

                    if (bookingExtraData.booking?.serviceArray?.length) {
                        // this.isLoadingServices = false;
                        this.bookingWithSelection = bookingExtraData;
                        this.bookingExtraData = bookingExtraData;
                    }
                },
                error: (err: Error): void => {
                    this.isLoading = false;
                    // this.isLoadingServices = false;
                    // this.isLoadingServicesError.active = true;
                    const message: string = Yii.t('wui-sasha-js', 'Erreur lors de la récupération du dossier : ') + "§§§" + err.message;
                    this.vehicleRentalNotification = {
                        type: NotificationType.Warning,
                        active: true,
                        message,
                        messageArray: message.split('§§§'),

                    };
                    console.error(err);
                }
            });
        } else {
            this.isLoading = false;
        }
    }

    private bookingLoadedError(error: Error): void {
        this.notification = {
            active: true,
            message: error.message,
            type: NotificationType.Error
        };

        this.tracingService.captureException(error);
        this.isLoading = false;
    }

    private configurationBookingStream(
        booking: TGetBookingResponse):
        Observable<[
            TGetBookingResponse,
            IGetEnumsService[],
            ISectorServicesConfig,
        ]> {
        return forkJoin([
            of(booking),
            booking.type ?
                booking.type.some((type: BookingType): boolean => type === BookingType.air) ?
                    this.enumsResourceService.listServices() :
                    of([]) :
                of([]),
            this.configurationResourceService.get<ISectorServicesConfig>(
                ['wui', 'app_sasha', 'config', 'booking-page', 'sector-services.json'],
                {
                    air: {},
                    stay: {}
                }
            ),
        ]);
    }
}
